/* eslint-disable max-len */
import React, { Fragment, useEffect, useState } from 'react';
import { Filter, ChartColumnBig } from 'lucide-react';

import { FilterPanel } from './FilterPanel';
import { CaseProcedure, CaseReportSummary } from '../../models/CaseTypes';
import CaseReportServiceFactory from '../../services/CaseReportServiceFactory'; 
import { sortCases } from '../../utils/caseUtils';
import { formatCaseDateDisplay, formatCaseDescription, formatSurgicalTeamDisplay } from '../../utils/caseUtils';

function FilterIcon(): React.JSX.Element {
  return (
    <div className="relative size-6">
      <Filter 
        className="absolute inset-0"
        size={20}
        style={{
          color: 'var(--high-emphasis-font)'
        }}
      />
    </div>
  );
}

interface DisplayCase {
  caseDesc: string;
  caseId: string;
  caseLevels: string;
  caseName: string;
  procedures: CaseProcedure[];
  doctorsDesc: string;
  date: string;
};

interface CaseRowProps {
  displayCase: DisplayCase,
  index: number;
  onCaseSelect: (displayCase: DisplayCase) => void;
}

/**
 * CaseRow:
 * Component representing a single row in the case library.
 */
function CaseRow({ displayCase, index, onCaseSelect }: CaseRowProps): React.JSX.Element {
  const [isPressed, setIsPressed] = useState(false);

  const handleMouseDown = (): void => {
    setIsPressed(true);
  };

  const handleMouseUp = (): void => {
    setIsPressed(false);
  };

  const handleClick = (): void => {
    onCaseSelect(displayCase);
  };

  // Determine background color based on state
  const getBackgroundClass = (): string => {
    if (isPressed) return 'bg-button-active';
    return 'bg-card hover:bg-button-hover';
  };

  return (
    <Fragment>
      {/* Mobile & Tablet Layout (< 915px) */}
      <div 
        className={`${getBackgroundClass()} w-full border-t border-border min-[915px]:hidden cursor-pointer min-h-24 min-[441px]:min-h-0 flex`}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="flex flex-col justify-center w-full flex-1">
          <div className="flex flex-col gap-[6px] items-start justify-center p-4 w-full">
            {/* Content Row with Icon, Procedure/Doctors, and Date */}
            <div className="flex items-center w-full">
              <div className="flex gap-4 items-center pr-2 flex-1 min-w-0">
                <div className="relative size-5 flex-shrink-0">
                  <ChartColumnBig 
                    className="absolute inset-0"
                    size={20}
                    style={{
                      color: 'var(--high-emphasis-font)'
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="leading-[normal] overflow-ellipsis overflow-hidden" style={{ fontVariationSettings: "'wdth' 100", color: 'var(--high-emphasis-font)' }}>
                    {displayCase.caseDesc}
                  </p>
                  <p className="text-[13px] leading-[normal] overflow-ellipsis overflow-hidden text-[#616161] text-nowrap" style={{ fontVariationSettings: "'wdth' 100" }}>
                    {displayCase.doctorsDesc}
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-center text-right w-[61px] flex-shrink-0">
                <p 
                  className="italic leading-[normal]" 
                  style={{ fontVariationSettings: "'wdth' 100", color: 'var(--high-emphasis-font)' }}
                  data-testid={`case-date-${index}`}
                >
                  {displayCase.date}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout (>= 915px) */}
      <div 
        className={`hidden min-[915px]:block ${getBackgroundClass()} w-full border-t border-border cursor-pointer`}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="flex flex-col justify-center size-full">
          <div className="flex items-center p-4 w-full">
            {/* Procedure Column */}
            <div className="flex gap-4 items-center pr-2 w-[719px]">
              <div className="relative size-6 flex-shrink-0">
                <ChartColumnBig 
                  className="absolute inset-0"
                  size={24}
                  style={{
                    color: 'var(--high-emphasis-font)'
                  }}
                />
              </div>
              <p className="flex-1 leading-[normal] overflow-ellipsis overflow-hidden text-nowrap" style={{ fontVariationSettings: "'wdth' 100", color: 'var(--high-emphasis-font)' }}>
                {displayCase.caseDesc}
              </p>
            </div>
            {/* Surgical Team Column */}
            <div className="w-[589px]">
              <p className="leading-[normal]" style={{ fontVariationSettings: "'wdth' 100", color: 'var(--high-emphasis-font)' }}>
                {displayCase.doctorsDesc}
              </p>
            </div>
            {/* Date Column */}
            <div className="w-[52px]">
              <p
                className="italic leading-[normal]"
                data-testid={`case-date-${index}`}
                style={{ fontVariationSettings: "'wdth' 100", color: 'var(--high-emphasis-font)' }}
              >
                {displayCase.date}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

interface CaseLibraryProps {
  env: string;
  onNavigate: (path: string) => void;
}

/**
 * CaseLibrary:
 * Main component for displaying the case library with filtering and sorting capabilities.
 */
export function CaseLibrary({ env, onNavigate }: CaseLibraryProps): React.JSX.Element {
  const caseReportServiceFactory = new CaseReportServiceFactory();
  const caseReportService = caseReportServiceFactory.create(env);

  const [cases, setCases] = useState<CaseReportSummary[]|null>(null);
  const [displayCases, setDisplayCases] = useState<DisplayCase[]>([]);
  const [doLoad, setDoLoad] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [procedureOptions, setProcedureOptions] = useState<{ value: string; label: string }[]>([]);

  const fetchCases = async (): Promise<void> => {
    try {
      setLoading(true);
      setDoLoad(false);
      setError('');
      const cases = await caseReportService.getCaseSummaries();
      sortCases(cases, 'date', 'desc');
      setCases(cases);

      const proceduresSet = new Set<string>();
      cases.forEach((caseItem) => {
        caseItem.procedures.forEach((proc) => {
          proceduresSet.add(proc.procedureType);
        });
      });
      const procedureOptionsArray = Array.from(proceduresSet).sort().map((procedure) => ({
        value: procedure,
        label: procedure,
      }));
      setProcedureOptions(procedureOptionsArray);

      const displayCases = mapCasesForDisplay(cases);
      setDisplayCases(displayCases);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProcedureChange = (procedure: string): void => {
    if (cases === null) return;

    let filteredCases = cases;

    if (procedure !== 'all') {
      filteredCases = cases.filter((caseItem) =>
        caseItem.procedures.some((proc) => proc.procedureType === procedure)
      );
    }

    const updatedDisplayCases = mapCasesForDisplay(filteredCases);
    setDisplayCases(updatedDisplayCases);
  };

  const handleSortChange = (sortValue: string): void => {
    if (cases === null) return;

    const [sortField, sortDirection] = sortValue.split('-');
    const sortedCases = sortCases(cases, sortField, sortDirection);
    const updatedDisplayCases = mapCasesForDisplay(sortedCases);
    setDisplayCases(updatedDisplayCases);
  };

  const mapCasesForDisplay = (caseSummaries: CaseReportSummary[]): DisplayCase[] => {
    return caseSummaries.map((caseItem) => ({
      caseId: caseItem.id,
      caseDesc: formatCaseDescription(caseItem, false),
      caseName: caseItem.name,
      caseLevels: caseItem.levels ? caseItem.levels : '',
      procedures: caseItem.procedures,
      doctorsDesc: formatSurgicalTeamDisplay(caseItem.actors),
      date: formatCaseDateDisplay(caseItem.date, true),
    } as DisplayCase));
  };

  const handleSelectCase = (selectedCase: DisplayCase): void => {
    onNavigate(`/cases-new/${selectedCase.caseName}`);
  };

  // Load all cases for user on initial render
  useEffect(() => {
    if (doLoad && cases === null && !loading) {
      fetchCases();
    }
  }, [cases, doLoad, loading, fetchCases]);

  return (
    <div className="flex flex-col min-h-screen bg-[#fcfcfc]">
      <main className="flex-1 w-full">
        <div className="flex flex-col items-center overflow-x-clip overflow-y-auto size-full mt-[0px] mr-[0px] mb-[24px] ml-[0px]">
          <div className="flex flex-col gap-3 items-center pb-0 pt-5 px-3 lg:px-5 w-full max-w-[1440px]">
            {/* Page Title - Mobile & Tablet */}
            <div className="flex gap-[10px] items-center justify-center w-full lg:hidden">
              <div className="flex-1">
                <h1>Case Library</h1>
              </div>
            </div>

            {/* Page Title - Desktop */}
            <div className="hidden lg:flex gap-[10px] items-center justify-center w-full">
              <div className="flex-1">
                <h1>Case Library</h1>
              </div>
            </div>

            {/* Section Header with Filter & List */}
            <div className="flex flex-col gap-4 w-full flex-1">
              {/* All Reports Header */}
              <div className="flex items-center justify-between w-full">
                <div className="flex-1">
                  <h2>All Performance Reports</h2>
                </div>
                <button
                  data-testid="filter-button"
                  className="flex items-center justify-center size-9 hover:bg-button-active rounded-lg transition-colors" 
                  aria-label="Filter"
                  onClick={() => setFilterOpen(true)}
                >
                  <div className="size-6 flex items-center justify-center">
                    <FilterIcon />
                  </div>
                </button>
              </div>

              {/* Column Headers and Case List Container */}
              <div className="flex flex-col gap-[6px] w-full">
                {/* Column Headers - Desktop Only */}
                <div className="hidden lg:flex w-full">
                  <div className="w-[735px]">
                    <p className="leading-[normal]" style={{ fontVariationSettings: "'wdth' 100", color: 'var(--medium-emphasis-font)', fontSize: 'var(--text-caption)' }}>
                      Procedure(s)
                    </p>
                  </div>
                  <div className="w-[590px]">
                    <p className="leading-[normal]" style={{ fontVariationSettings: "'wdth' 100", color: 'var(--medium-emphasis-font)', fontSize: 'var(--text-caption)' }}>
                      Surgical Team
                    </p>
                  </div>
                  <div className="w-[52px]">
                    <p className="leading-[normal] text-nowrap whitespace-pre" style={{ fontVariationSettings: "'wdth' 100", color: 'var(--medium-emphasis-font)', fontSize: 'var(--text-caption)' }}>
                      Date
                    </p>
                  </div>
                </div>

                {/* Case List */}
                <div className="flex flex-col items-start w-full">
                  {error &&
                    <p className="text-red-600 mb-4">{error}</p>
                  }
                  {displayCases.map((caseItem, index) => (
                    <CaseRow
                      key={index}
                      displayCase={caseItem}
                      index={index}
                      onCaseSelect={handleSelectCase}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <FilterPanel
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onProcedureChange={handleProcedureChange}
        onSortChange={handleSortChange}
        procedureOptions={procedureOptions}
      />
    </div>
  );
}
